import LsLoading from "Components/LsLoading";
import { Button, Modal, ModalBody, ModalHeader } from "Components/ReactstrapSlim";
import React, { useEffect, useMemo } from "react";
import { useState } from "react";
import { ActivityReservationInternalModel, MemberProfileViewModel, MembersClient } from "Services/API/API";
import { useApiClient } from "Services/API/useApiClient";
import classes from "./MemberProfile.module.scss"
import { AvatarCircle } from "Components/AvatarCircle";
import { Nav, NavLink, TabPane, TabContent } from "reactstrap";
import classNames from "classnames";
import { useActivity } from "../hooks";
import moment from "moment";
import * as Icons from "Components/Icons";
import { formatStringDate } from "Utils/date";
import { userSelector } from "Utils/selectors";
import { useSelector } from "react-redux";
import { VenueCalendarModal } from "Components/VenueCalendarModal";
import { FollowButton } from "Components/Connections/FollowButton";
import { formatReservationTime } from "Utils/datetime";

interface ActivityInfoCardProps {
   activity: ActivityReservationInternalModel;
   isOwner?: boolean;
   onPreBinOpen: (venueId: Guid) => void;
}

const ActivityInfoCard: React.FC<ActivityInfoCardProps> = ({ activity, isOwner, onPreBinOpen }) => {
   const isPast = (activity: ActivityReservationInternalModel) => activity.endDate && moment(activity.endDate).isBefore(moment());

   return <div className={classes.activityCard}>
      <div className={classes.activityInfo}>
         <div className={classes.activityTitle}>{formatStringDate(activity.startDate, "MMMM D, dddd")}</div>
         <div className={classes.text}>{`${isPast(activity) ? "worked at" : "is working at"} `} <a href={activity.venueXPPUrl} target="_blank">{activity.venueName}</a> {` from ${formatReservationTime(activity.startDate, activity.endDate)}`}</div>
      </div>
      <div className={classes.activityActions}>
         <Button color="primary" size="sm" onClick={() => onPreBinOpen(activity.venueId)}>{isOwner ? "Book Again" : "Work Here"}</Button>
      </div>
   </div>
}

export interface MemberProfileProps {
   isOpen: boolean;
   memberId: Guid;
   container?: string | HTMLElement;
   toggle: () => void;
}

type TabNames = "summary" | "activity";

export const MemberProfile: React.FC<MemberProfileProps> = ({ isOpen, memberId, container, toggle }) => {
   const [isLoading, setIsLoading] = useState(true);
   const [isOpenInternal, setIsOpenInternal] = useState(true);
   const [isPreBinOpen, setIsPreBinOpen] = useState(false);
   const [preBinVenueId, setPreBinVenueId] = useState<Guid | undefined>(undefined);
   const [activeTab, setActiveTab] = useState<TabNames>("summary");
   const memberClient = useApiClient(MembersClient);
   const user = useSelector(userSelector);
   const [memberProfile, setMemberProfile] = useState<MemberProfileViewModel | undefined>(undefined);
   const { activities } = useActivity();

   const isPast = (activity: ActivityReservationInternalModel) => activity.endDate && moment(activity.endDate).isBefore(moment());

   const activeReservations = useMemo(() => {
      return activities.filter(a => a.memberId == memberId && !isPast(a));
   }, [activities]);

   const pastReservations = useMemo(() => {
      return activities.filter(a => a.memberId == memberId && isPast(a));
   }, [activities]);

   const loadMemberProfile = async (signal: AbortSignal) => {
      setIsLoading(true);
      memberClient.getMemberProfile(memberId, signal)
         .then(data => setMemberProfile(data))
         .finally(() => setIsLoading(false));
   }

   const onPreBinOpen = (venueId: Guid) => {
      setIsOpenInternal(false);
      setPreBinVenueId(venueId);
      setIsPreBinOpen(true);
   }

   const onPreBinClose = () => {
      setIsOpenInternal(true);
      setPreBinVenueId(undefined);
      setIsPreBinOpen(false);
   }

   useEffect(() => {
      const abortController = new AbortController();
      loadMemberProfile(abortController.signal);
      return () => {
         abortController.abort();
      }
   }, []);

   return isLoading || !memberProfile
      ? <Modal isOpen={isLoading} container={container}>
         <ModalBody style={{ minHeight: "400px" }}>
            <LsLoading isLoading={isLoading} />
         </ModalBody>
      </Modal>
      : <><Modal isOpen={isOpenInternal && isOpen} toggle={toggle} container={container}>
         <ModalHeader toggle={toggle} className={classes.modalHeader}>
            <div className={classes.memberData}>
               <div className={classes.memberInfo}>
                  <div className={classes.memberInfoPicture}>
                     <AvatarCircle size="xl" colorIndex={0} {...memberProfile}></AvatarCircle>
                  </div>
                  <div className={classes.memberInfoData}>
                     <div>{memberProfile.firstName}</div>
                     <div>{memberProfile.lastName}</div>
                  </div>
               </div>
               <Nav className={classes.tabsNav}>
                  <NavLink className={classNames(classes.navLink, { [classes.navLinkActive]: activeTab == "summary" })} onClick={() => setActiveTab("summary")}>Summary</NavLink>
                  <NavLink className={classNames(classes.navLink, { [classes.navLinkActive]: activeTab == "activity" })} onClick={() => setActiveTab("activity")}>Activity</NavLink>
               </Nav>
            </div>
         </ModalHeader>
         <ModalBody className={classes.modalBody}>
            <TabContent activeTab={activeTab}>
               <TabPane tabId="summary">
                  <div className={classes.summary}>
                     <div className={classes.summaryBlock}>
                        <div className={classes.title}>{memberProfile.title}</div>
                        <div>
                           <div className={classes.text}>{memberProfile.email}</div>
                           <div className={classes.text}>{memberProfile.phoneNumber}</div>
                        </div>
                     </div>
                     <div className={classes.summaryBlock}>
                        <div className={classes.subtitle}>Reservations</div>
                        <div className={classes.overviewData}>
                           <div className={classes.calendar}><Icons.Calendar /></div>
                           <div className={classes.text}>{`${activeReservations.length} active reservation${activeReservations.length == 1 ? "" : "s"}, ${activeReservations.length + pastReservations.length} total`}</div>
                        </div>
                     </div>
                     {memberProfile.ratingCount > 0 && <div className={classes.summaryBlock}>
                        <div className={classes.subtitle}>Reviews</div>
                        <div className={classes.overviewData}>
                           <div className={classes.star}><Icons.Star /></div>
                           <div className={classes.text}><span>{`${memberProfile.averageRating?.toFixed(2)}/5`}</span> Average rating based on {memberProfile.ratingCount} reviews</div>
                        </div>
                     </div>}
                     {user.id != memberProfile.memberId && <div className={classes.summaryActions}>
                        <FollowButton targetMemberId={memberProfile.memberId} />
                     </div>}
                  </div>
               </TabPane>
               <TabPane tabId="activity">
                  <div className={classes.activity}>
                     <div className={classes.activityBlock}>
                        <div className={classes.title}>Past Reservations</div>
                        <div className={classes.activityList}>
                           {
                              pastReservations.map(activity => <ActivityInfoCard key={Guid.NewGuid()} activity={activity} isOwner={user.id == activity.memberId} onPreBinOpen={onPreBinOpen} />)
                           }
                           {
                              pastReservations.length == 0
                                 ? <div>No past reservations.</div>
                                 : null
                           }
                        </div>
                     </div>
                     <div className={classes.grayLine}></div>
                     <div className={classes.activityBlock}>
                        <div className={classes.title}>Upcoming Reservations</div>
                        <div className={classes.activityList}>
                           {
                              activeReservations.map(activity => <ActivityInfoCard key={Guid.NewGuid()} activity={activity} isOwner={user.id == activity.memberId} onPreBinOpen={onPreBinOpen} />)
                           }
                           {
                              activeReservations.length == 0
                                 ? memberProfile.memberId == user.id
                                    ? <div>No upcoming reservations. Change that! Make a reservation.</div>
                                    : <div>No upcoming reservations. Change that! Make a reservation and invite {memberProfile.firstName ?? memberProfile.email}.</div>
                                 : null
                           }
                        </div>
                     </div>
                  </div>
               </TabPane>
            </TabContent>
         </ModalBody>
      </Modal>
      {isPreBinOpen && preBinVenueId && <VenueCalendarModal isOpen={true} toggle={onPreBinClose} venueId={preBinVenueId} container={container} />}
      </>
}